<template>
  <div id="app" :class="appClasses">

    <div class="app-content">
      <div 
        class="app-header"
        :class="[{ 'sidebar-open': !sidebarCollapsed, 'sidebar-collapsed': sidebarCollapsed }]"
      >
        <div class="header-left">
          <CurrentSetScore
            :score="score"
            :sets-won="matchSetsWon"
            :sets-lost="matchSetsLost"
            :team-name="matchMetadata.teamName"
            :opposition-name="matchMetadata.opposition"
            :startPoint="currentPoint.startPoint"
          />
        </div>
        <div class="header-right">
          <button
            v-if="canUndoInitialStartPointSelection"
            class="btn header-btn destructive"
            type="button"
            @click="undoInitialStartPointSelection"
          >
            Undo Start Choice
          </button>
          <button
            v-if="showDuringRally"
            class="btn header-btn destructive"
            type="button"
            @click="handleRallyBack"
          >
            {{ currentPoint.startPoint === 'Serve' ? 'Revert Serve Action' : 'Revert Receive Action' }}
          </button>
          <button
            class="btn header-btn destructive"
            type="button"
            @click="undoLastConfirmedPoint"
            :disabled="!canUndoLastPoint"
          >
            Undo Last Point
          </button>
          <button class="btn header-btn destructive" @click="resetCurrentPointView">
            Reset Current Point
          </button>
          <button class="btn header-btn" @click="openMatchSummary">
            Match Summary
          </button>
          <div class="context-menu-container">
            <button class="btn header-btn icon-btn" @click.stop="toggleHeaderMenu">
              ⋮
            </button>
            <div v-if="showHeaderMenu" class="context-menu" @click.stop>
              <button class="btn context-menu-item destructive" @click="handleNewMatch">
                New Match (archive)
              </button>
              <button class="btn context-menu-item destructive" @click="handleDiscardMatch">
                New Match (discard)
              </button>
              <button class="btn context-menu-item" @click="handleOpenArchives">
                Saved Matches
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="showRosterSetup || !matchReady" class="setup-roster">
        <MatchSetup
          :initial-roster="matchRoster"
          :initial-match-info="matchMetadata"
          @match-setup="updateMatchRoster"
          @open-archives="openArchiveManager"
        />
      </div>

      <div v-else-if="!startingPlayersConfirmed" class="setup-starting-lineup">
        <SelectStartingPlayers
          :rosterPlayers="matchRoster.filter(player => player && player.playerType !== 'libero')"
          :liberoPool="matchLiberos"
          :setNumber="currentSetNumber"
          @update-starting-players="updateStartingPlayers"
          @back-to-roster="resetToRosterSelection"
        />
      </div>

      <div v-else class="stat-recording">
        <StartPoint
          v-if="showInitialStartPointSelector"
          :current-start-point="currentPoint.startPoint"
          @update-first-set="updateCurrentPoint"
          @update-selected-option="updateSelectedOption"
          :disabled="startPointDisabled || matchFinished"
          class="start-point-container"
          :class="{ 'actions-disabled': matchFinished }"
        />
        <div
          v-if="currentPoint.startPoint === 'Serve' && (!currentPoint.scoreAction.outcome || ['Ace', 'Serve error', 'Opposition Rotation Error', 'Rotation Error'].includes(currentPoint.scoreAction.pointDetail))"
          v-show="!showDuringRally"
          class="serve-action-container"
          :class="{ 'actions-disabled': matchFinished }"
        >
          <ServeAction
            ref="serveAction"
            :currentPoint="currentPoint"
            @update-first-set="updateCurrentPoint"
          />
        </div>
        <div
          v-if="currentPoint.startPoint === 'Receive' && (!currentPoint.scoreAction.outcome || ['Opp. serve error', 'Opp. ace', 'Opposition Rotation Error', 'Rotation Error'].includes(currentPoint.scoreAction.pointDetail))"
          v-show="!showDuringRally"
          class="receive-action-container"
          :class="{ 'actions-disabled': matchFinished }"
        >
          <ReceiveAction
            ref="receiveAction"
            :currentPoint="currentPoint"
            @update-first-set="updateCurrentPoint"
          />
        </div>
        <DuringRallyAction
          v-if="showDuringRally"
          key="during-rally-action"
          :current-point="currentPoint"
          :current-lineup="currentLineup"
          @update-during-rally="updateDuringRally"
          @terminal-action="handleTerminalAction"
          class="during-rally-action-container"
          :class="{ 'actions-disabled': matchFinished }"
        />
        <div v-if="(currentPoint.serveAction.player && currentPoint.serveAction.player.name === 'Opp. serve error') || currentPoint.serveAction.call || currentPoint.serveAction.rating !== null || currentPoint.receiveAction.call || currentPoint.receiveAction.rating !== null" class="score-action-container">
          <div
            v-if="showDuringRally && scoreActionCollapsed"
            class="score-action-toggle"
            @click="scoreActionCollapsed = false"
          >
            Score point manually &#x25B8;
          </div>
          <ScoreAction
            v-if="!isScoreActionDisabled && (!showDuringRally || !scoreActionCollapsed)"
            key="score-action"
            :initialOutcome="currentPoint.scoreAction.outcome"
            :initialPointDetail="currentPoint.scoreAction.pointDetail"
            :initialPlayer="currentPoint.scoreAction.player"
            :disabled="isScoreActionDisabled || matchFinished"
            :isPointDetailDisabled="isPointDetailDisabled || matchFinished"
            :isPlayerDisabled="isPlayerDisabled || matchFinished"
            :current-start-point="currentPoint.startPoint"
            :serveRating="currentPoint.serveAction.rating"
            :passRating="currentPoint.receiveAction.rating"
            @update-first-set="updateCurrentPoint"
          />
        </div>
      </div>

      <CurrentPointTable
        v-if="startingPlayersConfirmed"
        v-show="false"
        :current-point="currentPoint"
        :current-lineup="currentLineup"
      />

      <PlayByPlayGrid v-show="false" v-if="startingPlayersConfirmed" :playByPlay="currentPlayByPlay" :current-lineup="currentLineup" />

      <StatsDisplay
        v-if="startingPlayersConfirmed && !showRosterSetup"
        :is-collapsed="statsCollapsed"
        :fullscreen="statsFullscreen"
        :sidebar-collapsed="sidebarCollapsed"
        @toggle-collapsed="onStatsToggle"
        @toggle-fullscreen="onStatsFullscreenToggle"
      />
    </div>

    <MatchSummary
      v-if="isMatchSummaryVisible"
      :current-lineup="currentLineup"
      :sidebar-collapsed="sidebarCollapsed"
      @close="closeMatchSummary"
      @new-match="archiveAndResetMatch"
      @open-archives="openArchiveManager"
    />

    <SetSummary
      v-if="showSetSummary"
      :set-data="currentSet"
      :current-lineup="currentLineup"
      :can-start-next-set="!matchFinished"
      :match-finished="matchFinished"
      @undo-last-point="undoLastConfirmedPoint"
      @start-next-set="startNextSet"
      @complete-match="completeMatch"
    />

    <PointOutcomeOverlay
      :outcome="pointOutcome"
      :visible="!!pointOutcome"
      @dismiss="dismissPointOutcome"
    />

    <ArchiveManager
      v-if="showArchiveManager"
      @close="closeArchiveManager"
      @restore="restoreArchivedMatch"
    />

    <ActionLogSidebar
      v-if="startingPlayersConfirmed && !showRosterSetup"
      :is-collapsed="sidebarCollapsed"
      :points="currentPlayByPlay"
      :current-point="currentPoint"
      :current-lineup="currentLineup"
      :last-confirmed-point-id="lastConfirmedPointId"
      :sidebar-collapsed="sidebarCollapsed"
      :match-finished="matchFinished"
      @toggle-collapsed="toggleSidebar"
      @player-substituted="handlePlayerSubstitution"
      @libero-swap-back="handleLiberoSwapBack"
      @libero-sub-in="handleLiberoSubIn"
      @libero-swap="handleLiberoSwap"
      @update-starting-players="updateStartingPlayers"
    />

  </div>
</template>

<script>
import StartPoint from './components/StartPoint.vue'
import ServeAction from './components/ServeAction.vue'
import ReceiveAction from './components/ReceiveAction.vue'
import ScoreAction from './components/ScoreAction.vue'
import DuringRallyAction from './components/DuringRallyAction.vue'
import CurrentSetScore from './components/CurrentSetScore.vue'
import SelectStartingPlayers from './components/SelectStartingPlayers.vue'
import MatchSetup from './components/MatchSetup.vue'
import PlayByPlayGrid from './components/PlayByPlayGrid.vue';
import StatsDisplay from './components/StatsDisplay.vue';
import CurrentPointTable from './components/CurrentPointTable.vue';
import MatchSummary from './components/MatchSummary.vue';
import SetSummary from './components/SetSummary.vue';
import ArchiveManager from './components/ArchiveManager.vue';
import ActionLogSidebar from './components/ActionLogSidebar.vue';
import PointOutcomeOverlay from './components/PointOutcomeOverlay.vue';
import { ref } from 'vue';
import { getDefaultCurrentPoint } from './composables/useMigration.js';
import { useUIState } from './composables/useUIState.js';
import { usePersistence } from './composables/usePersistence.js';
import { useCurrentPoint } from './composables/useCurrentPoint.js';
import { useRotation } from './composables/useRotation.js';
import { useScoring } from './composables/useScoring.js';
import { usePointWatchers } from './composables/usePointWatchers.js';

export default {
  name: 'App',
  components: {
    StartPoint,
    ServeAction,
    ReceiveAction,
    ScoreAction,
    DuringRallyAction,
    CurrentSetScore,
    SelectStartingPlayers,
    MatchSetup,
    PlayByPlayGrid,
    StatsDisplay,
    CurrentPointTable,
    MatchSummary,
    SetSummary,
    ArchiveManager,
    ActionLogSidebar,
    PointOutcomeOverlay
  },
  setup() {
    // Refs for state that will move to other composables in later phases
    const startingPlayersConfirmed = ref(false);
    const currentLineup = ref([]);

    const ui = useUIState({ startingPlayersConfirmed, currentLineup });

    return {
      startingPlayersConfirmed,
      currentLineup,
      ...ui
    };
  },
  data() {
    return {
      currentPoint: getDefaultCurrentPoint(),
      matchStartServeReceive: '',
      matchTiebreakServeReceive: '',
      startPointDisabled: false,
      score: { won: 0, lost: 0 },
      matchRoster: [],
      matchLiberos: [],
      autoConfirming: false,
      isUndoing: false,
      lastConfirmedPointId: null,
      processingTerminalAction: false
    };
  },
  created() {
    const persistence = usePersistence(this);
    this._persistence = persistence;
    const currentPoint = useCurrentPoint(this);
    this._currentPoint = currentPoint;
    // Expose composable methods on instance for template bindings
    this.updateCurrentPoint = currentPoint.updateCurrentPoint;
    this.updateSelectedOption = currentPoint.updateSelectedOption;
    this.undoInitialStartPointSelection = currentPoint.undoInitialStartPointSelection;
    this.updateDuringRally = currentPoint.updateDuringRally;
    this.handleRallyBack = currentPoint.handleRallyBack;
    this.resetCurrentPointView = currentPoint.resetCurrentPointView;
    this.updatePreviewScore = currentPoint.updatePreviewScore;
    this.syncRallyActive = currentPoint.syncRallyActive;
    const rotation = useRotation(this);
    this._rotation = rotation;
    // Expose rotation methods on instance for template bindings
    this.updateStartingPlayers = rotation.updateStartingPlayers;
    this.updateMatchRoster = rotation.updateMatchRoster;
    this.resetToRosterSelection = rotation.resetToRosterSelection;
    this.handlePlayerSubstitution = rotation.handlePlayerSubstitution;
    this.handleLiberoSwapBack = rotation.handleLiberoSwapBack;
    this.handleLiberoSubIn = rotation.handleLiberoSubIn;
    this.handleLiberoSwap = rotation.handleLiberoSwap;
    this.applyLiberoSubstitution = rotation.applyLiberoSubstitution;
    const scoring = useScoring(this);
    this._scoring = scoring;
    // Expose scoring methods on instance for template bindings
    this.confirmSelections = scoring.confirmSelections;
    this.autoConfirm = scoring.autoConfirm;
    this.undoLastConfirmedPoint = scoring.undoLastConfirmedPoint;
    this.startNextSet = scoring.startNextSet;
    this.completeMatch = scoring.completeMatch;
    this.clearLocalStorage = scoring.clearLocalStorage;
    this.archiveAndResetMatch = scoring.archiveAndResetMatch;
    this.restoreArchivedMatch = scoring.restoreArchivedMatch;
    this.setWorkflowCallbacks({
      archiveAndResetMatch: () => this.archiveAndResetMatch(),
      clearLocalStorage: () => this.clearLocalStorage()
    });
    persistence.loadPersistedState();
    persistence.setupPersistenceWatchers();
    persistence.backfillArchivedMatchesData();
    const pointWatchers = usePointWatchers(this);
    pointWatchers.setup();
  },
  computed: {
    canUndoLastPoint() {
      return this.currentPlayByPlay.length > 0;
    },
    formattedFirstSet() {
      return JSON.stringify(this.currentPoint, null, 2);
    },
    isScoreActionDisabled() {
      return this.currentPoint.serveAction.rating === 4 ||
        this.currentPoint.serveAction.rating === 0 ||
        this.currentPoint.receiveAction.rating === 0 ||
        Boolean(this.currentPoint.serveAction.call) ||
        Boolean(this.currentPoint.receiveAction.call);
    },
    isPointDetailDisabled() {
      return this.currentPoint.serveAction.rating === 4 ||
        this.currentPoint.serveAction.rating === 0 ||
        this.currentPoint.receiveAction.rating === 0 ||
        Boolean(this.currentPoint.serveAction.call) ||
        Boolean(this.currentPoint.receiveAction.call);
    },
    isPlayerDisabled() {
      return this.currentPoint.serveAction.rating === 4 ||
        this.currentPoint.serveAction.rating === 0 ||
        this.currentPoint.receiveAction.rating === 0 ||
        Boolean(this.currentPoint.serveAction.call) ||
        Boolean(this.currentPoint.receiveAction.call);
    },
    isConfirmDisabled() {
      if (this.matchFinished) {
        return true;
      }
      const hasServeCall = Boolean(this.currentPoint.serveAction.call);
      const hasReceiveCall = Boolean(this.currentPoint.receiveAction.call);
      const serveActionFilled = this.currentPoint.serveAction.player &&
        (this.currentPoint.serveAction.player.name || this.currentPoint.serveAction.player === 'Team') &&
        (this.currentPoint.serveAction.rating !== null || hasServeCall);
      const receiveActionFilled = this.currentPoint.receiveAction.player &&
        (this.currentPoint.receiveAction.player.name || this.currentPoint.receiveAction.player === 'Team') &&
        (this.currentPoint.receiveAction.rating !== null || hasReceiveCall);
    
      const relevantPointDetails = [
        'Kill',
        'Kill block (solo)',
        'Kill block (assist)',
        'Ace',
        'Attack error',
        'Block error',
        'Serve error',
        'Other error',
        'Opp. kill block',
        'Opp. ace'
      ];
      
      const scoreActionFilled = this.currentPoint.scoreAction.outcome && this.currentPoint.scoreAction.pointDetail;
      const playerRequired = relevantPointDetails.includes(this.currentPoint.scoreAction.pointDetail);
      const assistRequired = this.currentPoint.scoreAction.pointDetail === 'Kill block (assist)';
      const primaryPlayerFilled = this.currentPoint.scoreAction.player && (this.currentPoint.scoreAction.player.name || this.currentPoint.scoreAction.player === 'Team');
      const secondaryPlayerFilled = this.currentPoint.scoreAction.secondPlayer && (this.currentPoint.scoreAction.secondPlayer.name || this.currentPoint.scoreAction.secondPlayer === 'Team');
      const playerFilled = playerRequired
        ? (assistRequired ? (primaryPlayerFilled && secondaryPlayerFilled) : primaryPlayerFilled)
        : true;

      const oppServeError = this.currentPoint.receiveAction.player && this.currentPoint.receiveAction.player.name === 'Opp. serve error';
      const teamReceiveError = this.currentPoint.receiveAction.player === 'Team receive error';
      const receiveRotationError = hasReceiveCall;

      if (oppServeError || teamReceiveError || receiveRotationError) {
        return false;
      }

      return !((serveActionFilled || receiveActionFilled) && scoreActionFilled && playerFilled);
    },
    showDuringRally() {
      if (this.currentPoint.rallyActive === false) {
        return false;
      }
      if (this.currentPoint.scoreAction && this.currentPoint.scoreAction.outcome) {
        return false;
      }
      if (this.currentPoint.startPoint === 'Serve') {
        const { rating, call } = this.currentPoint.serveAction;
        return rating !== null && rating !== 0 && rating !== 4 && !call;
      }
      if (this.currentPoint.startPoint === 'Receive') {
        const player = this.currentPoint.receiveAction.player;
        if (player && player.name === 'Opp. serve error') {
          return false;
        }
        if (player === 'Team receive error') {
          return false;
        }
        const { rating, call } = this.currentPoint.receiveAction;
        return rating !== null && rating !== 0 && !call;
      }
      return false;
    },
    showInitialStartPointSelector() {
      const hasPointAction =
        this.currentPoint.serveAction.rating !== null ||
        this.currentPoint.serveAction.call ||
        this.currentPoint.receiveAction.rating !== null ||
        this.currentPoint.receiveAction.call ||
        Boolean(this.currentPoint.scoreAction.outcome);
      const isSetStartWithNoPoints = this.currentPlayByPlay.length === 0;
      const isMatchStart = this.currentSetNumber === 1;
      const isTieBreakStart = this.currentSetNumber === 5 && this.matchSetsWon === 2 && this.matchSetsLost === 2;
      const needsStartPointSelection = isSetStartWithNoPoints && (isMatchStart || isTieBreakStart);
      return needsStartPointSelection && !this.showDuringRally && !hasPointAction && !this.currentPoint.startPoint;
    },
    canUndoInitialStartPointSelection() {
      const hasPointAction =
        this.currentPoint.serveAction.rating !== null ||
        this.currentPoint.serveAction.call ||
        this.currentPoint.receiveAction.rating !== null ||
        this.currentPoint.receiveAction.call ||
        Boolean(this.currentPoint.scoreAction.outcome);
      const isSetStartWithNoPoints = this.currentPlayByPlay.length === 0;
      const isMatchStart = this.currentSetNumber === 1;
      const isTieBreakStart = this.currentSetNumber === 5 && this.matchSetsWon === 2 && this.matchSetsLost === 2;
      const canUndoStartPointSelection = isSetStartWithNoPoints && (isMatchStart || isTieBreakStart);
      return canUndoStartPointSelection && !hasPointAction && Boolean(this.currentPoint.startPoint) && !this.startPointDisabled;
    }
  },
  methods: {
    handleTerminalAction(payload) {
      console.log('handleTerminalAction called', {
        pointId: this.currentPoint.id,
        lastConfirmedPointId: this.lastConfirmedPointId,
        payload
      });

      if (this.matchFinished || this.processingTerminalAction) {
        console.log('handleTerminalAction: Blocked by matchFinished or processingTerminalAction');
        return;
      }

      // Prevent duplicate confirmations
      if (this.lastConfirmedPointId === this.currentPoint.id) {
        console.log('handleTerminalAction: Blocked - point already confirmed');
        return;
      }

      this.processingTerminalAction = true;

      // Set scoreAction directly
      this.currentPoint.scoreAction = {
        outcome: payload.outcome,
        pointDetail: payload.pointDetail,
        player: payload.player,
        secondPlayer: payload.secondPlayer,
        attackSubtype: payload.attackSubtype || null,
        attackErrorSubtype: payload.attackErrorSubtype || null
      };

      // Immediately confirm the point
      this.$nextTick(() => {
        // Double-check we haven't already confirmed this point
        if (this.lastConfirmedPointId !== this.currentPoint.id) {
          console.log('handleTerminalAction: Calling confirmSelections');
          this.confirmSelections();
        } else {
          console.log('handleTerminalAction: Skipping confirmSelections - already confirmed');
        }

        this.$nextTick(() => {
          this.processingTerminalAction = false;
        });
      });
    }
  }
}
</script>
